"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import { isAssistantReturn } from "@/lib/auth-return";
import { captureAssistantEvent } from "@/lib/analytics/assistant-funnel";
import { registerAction } from "./actions";
import { initialRegisterState, type RegisterState } from "./state";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, FieldRow, Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { AuthFooter, AuthFooterLink, AuthHeading } from "@/components/auth/AuthCardParts";
import { type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { humanizeFieldName, type RegisterExtraField } from "@/lib/lecturehub/register-fields";

function fieldError(state: RegisterState, name: string): string | undefined {
  if (state.ok) return undefined;
  return state.errors?.[name]?.[0];
}

type Props = {
  lang: Lang;
  next: string;
  policy: { requireTerms: boolean; requireGdpr: boolean };
  prefill?: { name: string; email: string } | null;
  extras?: RegisterExtraField[];
  /** `/login?redirect=…` for the footer — computed by the page from `next`. */
  loginHref: string;
};

/**
 * The register CARD, heading and footer included — the page leaves those to
 * this component because they change with the form's state: "Create your
 * account" while the form is up, "Check your inbox" once it has been sent,
 * each with its own footer line. The layout's side panel stays static, so its
 * copy is a welcome, not a repeat of either heading.
 *
 *  `policy` is the portal's registration policy (`portal.registration`):
 *  which consent boxes to render. The action re-checks it server-side.
 *  `prefill` is the newsletter form's guest handoff (name/email typed there);
 *  when present the newsletter opt-in arrives pre-ticked — that intent is why
 *  they came. `extras` are the portal's own register arguments beyond the
 *  built-ins (O44 — N24's `salutation`), rendered after the name row; the
 *  action re-reads the same list from the schema, never from the form.
 */
export function RegisterForm(props: Props) {
  // "Use a different address" remounts the card: `useActionState` has no reset,
  // and a fresh mount is exactly the empty form the user asked for.
  const [attempt, setAttempt] = useState(0);
  return <RegisterCard key={attempt} {...props} onStartOver={() => setAttempt((n) => n + 1)} />;
}

function RegisterCard({
  lang,
  next,
  policy,
  prefill,
  extras = [],
  loginHref,
  onStartOver,
}: Props & { onStartOver: () => void }) {
  const t = useDict();
  const [state, formAction, pending] = useActionState<RegisterState, FormData>(
    registerAction,
    initialRegisterState,
  );

  const reported = useRef(false);
  useEffect(() => {
    if (!state.ok || reported.current || !isAssistantReturn(next)) return;
    reported.current = true;
    captureAssistantEvent("assistant_registration_completed", { pending_approval: state.pending });
  }, [state, next]);

  if (state.ok) {
    // Every registration type sends the confirmation email first; on an
    // `admin_confirmation` portal an approval step follows it.
    const [before, after] = t.auth.checkInboxBodyTpl.split("{email}");
    return (
      <>
        <AuthHeading title={state.pending ? t.auth.pendingApprovalTitle : t.auth.checkInboxTitle} />
        <div className="mt-6 space-y-4 text-body leading-relaxed">
          <p>
            {before}
            <strong className="text-foreground">{state.email}</strong>
            {after}
          </p>
          {state.pending ? <p>{t.auth.pendingApproval}</p> : null}
          <p className="text-mid-gray text-sm">{t.auth.checkInboxHint}</p>
          <div className="pt-1">
            <Button type="button" variant="secondary" onClick={onStartOver}>
              {t.auth.startOver}
            </Button>
          </div>
        </div>
        <AuthFooter>
          <p className="text-body">
            {t.auth.alreadyConfirmed}{" "}
            <AuthFooterLink href={loginHref}>{t.auth.signIn}</AuthFooterLink>
          </p>
        </AuthFooter>
      </>
    );
  }

  return (
    <>
      <AuthHeading title={t.auth.createAccount} subtitle={t.auth.registerSubtitle} />
      <form action={formAction} className="mt-8 grid gap-4">
        <input type="hidden" name="lang" value={lang} />
        <input type="hidden" name="redirect" value={next} />
        {state.error ? <Alert tone="error">{state.error}</Alert> : null}

        <div className="grid sm:grid-cols-2 gap-4">
          <FieldRow label={t.auth.firstName} error={fieldError(state, "name")}>
            <Input
              name="name"
              autoComplete="given-name"
              required
              defaultValue={prefill?.name || undefined}
              className="rounded-xl"
            />
          </FieldRow>
          <FieldRow label={t.auth.lastName} error={fieldError(state, "surname")}>
            <Input name="surname" autoComplete="family-name" required className="rounded-xl" />
          </FieldRow>
        </div>

        {extras.length ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {extras.map((f) =>
              f.kind === "Boolean" ? (
                <Checkbox
                  key={f.name}
                  name={f.name}
                  label={humanizeFieldName(f.name)}
                  required={f.required}
                />
              ) : (
                <FieldRow
                  key={f.name}
                  label={humanizeFieldName(f.name)}
                  error={fieldError(state, f.name)}
                >
                  <Input name={f.name} required={f.required} className="rounded-xl" />
                </FieldRow>
              ),
            )}
          </div>
        ) : null}

        <FieldRow label={t.auth.email} error={fieldError(state, "email")}>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            defaultValue={prefill?.email || undefined}
            className="rounded-xl"
          />
        </FieldRow>

        <div className="grid gap-4 sm:grid-cols-2">
          <FieldRow
            label={t.auth.password}
            hint={t.auth.passwordHint}
            error={fieldError(state, "password")}
          >
            <PasswordInput
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="rounded-xl"
            />
          </FieldRow>

          <FieldRow label={t.auth.repeatPassword} error={fieldError(state, "repeat_password")}>
            <PasswordInput
              name="repeat_password"
              autoComplete="new-password"
              required
              minLength={8}
              className="rounded-xl"
            />
          </FieldRow>
        </div>

        {policy.requireGdpr ? <Checkbox name="gdpr" label={t.auth.gdprAccept} required /> : null}
        {policy.requireTerms ? <Checkbox name="terms" label={t.auth.termsAccept} required /> : null}
        <Checkbox name="newsletter" label={t.auth.newsletterOptIn} defaultChecked={!!prefill} />

        <Button type="submit" loading={pending} className="mt-1 w-full">
          {t.auth.createAccount}
        </Button>
      </form>
      <AuthFooter>
        <p className="text-body">
          {t.auth.haveAccount} <AuthFooterLink href={loginHref}>{t.auth.signIn}</AuthFooterLink>
        </p>
      </AuthFooter>
    </>
  );
}
